import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Paper, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Close, LocalFireDepartment, EmojiEvents } from '@mui/icons-material';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { CoinIcon } from './BMLCoinBalance';
import { RADIUS } from '../../theme/tokens';

// Themed particle dots — no emoji, no new libs
function Particle({ index, color, reducedMotion }) {
  if (reducedMotion) return null;

  const startX = Math.round((index / 8) * 100);
  const endX = startX + (Math.random() * 40 - 20);

  return (
    <motion.div
      initial={{ y: '80%', x: `${startX}%`, opacity: 0, scale: 0.6 }}
      animate={{
        y: [null, '-10%'],
        opacity: [0, 0.9, 0],
        scale: [0.6, 1, 0.4],
      }}
      transition={{
        duration: 1.8 + Math.random() * 0.8,
        delay: index * 0.12,
        repeat: Infinity,
        repeatDelay: 0.8,
        ease: 'easeOut',
      }}
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: color,
        pointerEvents: 'none',
      }}
    />
  );
}

/**
 * CoinEarnedAnimation
 * Fixed-position toast shown when coins are awarded.
 * Replaces emoji-burst with themed circular particle dots.
 * Respects prefers-reduced-motion.
 *
 * @param {boolean} show
 * @param {() => void} [onClose]
 * @param {Array<{amount, description}>} [rewards]
 * @param {number} [totalEarned]
 * @param {number} [streak]
 * @param {boolean} [isStreakBonus]
 * @param {number} [autoHideDuration]
 */
const CoinEarnedAnimation = ({
  show,
  onClose,
  rewards = [],
  totalEarned = 0,
  streak = 0,
  isStreakBonus = false,
  autoHideDuration = 5000,
}) => {
  const theme = useTheme();
  const reducedMotion = useReducedMotionPreferred();
  const [visible, setVisible] = useState(show);

  const accentColor = theme.palette.accent?.main ?? theme.palette.warning.main;
  const tintBg = theme.palette.mode === 'light' ? 'rgba(247,179,43,0.10)' : 'rgba(255,202,92,0.12)';
  const tintBorder = theme.palette.mode === 'light' ? 'rgba(247,179,43,0.4)' : 'rgba(255,202,92,0.3)';
  const coinTextColor = theme.palette.mode === 'light' ? 'rgba(120,80,0,0.9)' : accentColor;

  useEffect(() => {
    setVisible(show);
    if (show && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [show, autoHideDuration, onClose]);

  const handleClose = () => {
    setVisible(false);
    if (onClose) onClose();
  };

  const entryVariants = reducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0.12 },
      }
    : {
        initial: { opacity: 0, y: -80, scale: 0.85 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -40, scale: 0.9 },
        transition: { type: 'spring', stiffness: 280, damping: 24 },
      };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          {...entryVariants}
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            width: '90%',
            maxWidth: 400,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              bgcolor: tintBg,
              border: `2px solid ${tintBorder}`,
              borderRadius: RADIUS.xl,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Close notification"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'text.secondary' }}
            >
              <Close fontSize="small" />
            </IconButton>

            {/* Particle field */}
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              {[...Array(8)].map((_, i) => (
                <Particle key={i} index={i} color={accentColor} reducedMotion={reducedMotion} />
              ))}
            </Box>

            <Box sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
              {/* Coin icon — scale pulse or static on reduced-motion */}
              <motion.div
                animate={
                  reducedMotion
                    ? {}
                    : { scale: [1, 1.18, 1], rotate: [0, 8, -8, 0] }
                }
                transition={{ duration: 0.45, repeat: 2 }}
                style={{ display: 'inline-block', marginBottom: 8 }}
              >
                <CoinIcon size={48} />
              </motion.div>

              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: coinTextColor, mb: 0.5 }}
              >
                +{totalEarned} BML Coins!
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                {rewards.map((reward, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      +{reward.amount} — {reward.description}
                    </Typography>
                  </motion.div>
                ))}
              </Box>

              {streak > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 2,
                      px: 2,
                      py: 0.5,
                      bgcolor: isStreakBonus ? 'warning.main' : 'warning.light',
                      borderRadius: RADIUS.pill,
                      color: isStreakBonus ? '#FFFFFF' : 'warning.dark',
                    }}
                  >
                    <LocalFireDepartment fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      {streak} day streak!
                    </Typography>
                    {isStreakBonus && <EmojiEvents fontSize="small" />}
                  </Box>
                </motion.div>
              )}
            </Box>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CoinEarnedAnimation;
